import * as React from 'react';
import Picture from '../picture/picture';
import type { AvatarProps, AvatarSize } from './avatar.type';
import { ProfileOutline, Text, type TextProps } from '@components/atoms';
import { cn } from '@utils';

const MAX_INITIALS = 2;

const sizeClassMap: Record<AvatarSize, string> = {
	sm: 'w-7 h-7',
	md: 'w-9 h-9',
	lg: 'w-[4.8rem] h-[4.8rem]',
	xl: 'w-[6.4rem] h-[6.4rem]',
};

const iconSizeClassMap: Record<AvatarSize, string> = {
	sm: 'w-3 h-3',
	md: 'w-4 h-4',
	lg: 'w-5 h-5',
	xl: 'w-7 h-7',
};

const textSizeMap: Record<AvatarSize, NonNullable<TextProps['size']>> = {
	sm: 'xs',
	md: 'sm',
	lg: 'md',
	xl: 'lg',
};

function getInitials(name: string): string {
	if (name.length <= 3) {
		return name;
	}

	const parts: string[] = name.trim().split(/\s+/).filter(Boolean);

	if (!parts.length) {
		return '';
	}

	const initials: string[] = parts.slice(0, MAX_INITIALS).map(part => part[0]?.toUpperCase() ?? '');

	return initials.join('');
}

export const Avatar: React.VoidFunctionComponent<AvatarProps> = ({
	accessibleName,
	name,
	label,
	imageUrl,
	size = 'md',
	className,
	onClick,
	isDisabled,
}) => {
	const resolvedAccessibleName: string = accessibleName || label || name || '';
	const isInteractive: boolean = Boolean(onClick) && !isDisabled;

	const baseClass: string = cn(
		'flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full transition-transform duration-200',
		isInteractive && 'cursor-pointer hover:scale-110',
		isDisabled && 'pointer-events-none',
		sizeClassMap[size],
		className
	);

	const handleClick: React.MouseEventHandler<HTMLElement> | undefined = isInteractive ? onClick : undefined;

	const initials: string = !imageUrl && name ? getInitials(name) : '';

	const variantClass: string = imageUrl ? 'border border-primary' : 'border border-primary bg-white text-primary';

	const testId: string = imageUrl ? 'avatar-image' : initials ? 'avatar-initials' : 'avatar-fallback';

	if (imageUrl) {
		if (onClick) {
			return (
				<button
					type="button"
					className={cn(baseClass, variantClass)}
					aria-label={resolvedAccessibleName}
					data-testid={testId}
					onClick={handleClick}
					disabled={isDisabled}
				>
					{imageUrl ? (
						<Picture path={imageUrl} alt={resolvedAccessibleName} className="h-full w-full object-cover" />
					) : (
						<Text size="xs" color="primary">
							{initials}
						</Text>
					)}
				</button>
			);
		}

		return imageUrl ? (
			<Picture path={imageUrl} alt={resolvedAccessibleName} className={cn(baseClass, variantClass)} />
		) : (
			<Text size="xs" color="primary">
				{initials}
			</Text>
		);
	}

	return (
		<button
			type="button"
			className={cn(baseClass, variantClass)}
			aria-label={resolvedAccessibleName}
			data-testid={testId}
			onClick={handleClick}
			disabled={isDisabled}
		>
			{initials ? (
				<Text size={textSizeMap[size]} weight="bold" color="primary" className="leading-none uppercase">
					{initials}
				</Text>
			) : (
				<ProfileOutline className={iconSizeClassMap[size]} />
			)}
		</button>
	);
};
